import { castleSideOf } from '../game/castling.js';
import { fileOf, rowOf, squareAt } from '../game/squares.js';
import { EMPTY, FLAG_EN_PASSANT, type Move, type Square } from '../game/types.js';
import { PIECE_FADE_DURATION_MS, PIECE_MOVE_DURATION_MS } from '../utils/constants.js';
import { squareToCanvasCenter, type Point, type ViewerRotation } from './geometry.js';

interface Slide {
  /** Code drawn while sliding — the pre-promotion pawn for a promotion. */
  readonly code: number;
  readonly from: Square;
  readonly to: Square;
}

interface Fade {
  readonly code: number;
  readonly square: Square;
}

/**
 * How to animate one move over the post-move board: the renderer hides each
 * slide's destination square, draws the slides in flight, and fades captures
 * out where they stood (the passed pawn's own square for en passant).
 */
export interface MoveAnimationPlan {
  readonly slides: readonly Slide[];
  readonly fades: readonly Fade[];
  readonly totalMs: number;
}

export interface MoveAnimationFrame {
  readonly slides: readonly { readonly code: number; readonly at: Point }[];
  readonly fades: readonly { readonly code: number; readonly square: Square; readonly alpha: number }[];
  readonly done: boolean;
}

export function planMoveAnimation(move: Move, board: Int8Array): MoveAnimationPlan {
  const slides: Slide[] = [{ code: move.piece, from: move.from, to: move.to }];
  const castle = castleSideOf(move);
  if (castle !== undefined) {
    slides.push({ code: board[castle.rookFrom] ?? EMPTY, from: castle.rookFrom, to: castle.rookTo });
  }
  const fades: Fade[] = [];
  if (move.captured !== EMPTY) {
    const square = (move.flags & FLAG_EN_PASSANT) !== 0 ? squareAt(fileOf(move.to), rowOf(move.from)) : move.to;
    fades.push({ code: move.captured, square });
  }
  return { slides, fades, totalMs: Math.max(PIECE_MOVE_DURATION_MS, fades.length > 0 ? PIECE_FADE_DURATION_MS : 0) };
}

/** Squares to leave empty on the post-move board while the plan runs. */
export function animatedSquares(plan: MoveAnimationPlan): Set<Square> {
  return new Set(plan.slides.map((s) => s.to));
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

export function frameAt(plan: MoveAnimationPlan, elapsedMs: number, rotation: ViewerRotation): MoveAnimationFrame {
  const slideT = easeOutCubic(clamp01(elapsedMs / PIECE_MOVE_DURATION_MS));
  const fadeT = clamp01(elapsedMs / PIECE_FADE_DURATION_MS);
  return {
    slides: plan.slides.map((s) => {
      const a = squareToCanvasCenter(s.from, rotation);
      const b = squareToCanvasCenter(s.to, rotation);
      return { code: s.code, at: { x: a.x + (b.x - a.x) * slideT, y: a.y + (b.y - a.y) * slideT } };
    }),
    fades: plan.fades.map((f) => ({ code: f.code, square: f.square, alpha: 1 - fadeT })),
    done: elapsedMs >= plan.totalMs,
  };
}
